import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Alert, AlertType } from "@/atoms-react/alert/index.react";
import { Button } from "@/atoms-react/button/Button.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base, InfoAlert } from "@/atoms-react/text/Text.react";
import { useKYCContext } from "@/features/auth/hooks/use-kyc-context";
import { role } from "@/features/auth/types";
import { useCompany } from "@/features/company/hooks/use-company";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { AgentNatureMandate, OlkyPassProLinkType } from "@/features/company/types";
import { KYCStatusType } from "@/features/kyc/types";
import { useLinks } from "@/features/user/hooks/use-links";
import { useUser } from "@/features/user/hooks/use-user";
import { useUserCheckAccess } from "@/features/user/hooks/use-user-check-access";
import { useAppSelector } from "@/redux/hooks";
import { getUserRoles } from "@/redux/slices/userSlice";
import { Themes } from "@/types/theme";
import { LayoutTitle } from "../../../_layout/index.react";
import { OlkypassCard } from "../../../dashboard/components/olkypass-cards/index.react";
import { IdentityHeaderCard } from "../identity-header-card/index.react";
import { LevelCard } from "../level-card-status-container/index.react";
import { LevelSupport } from "../level-support/index.react";

type LevelProps = {
  isOnboarding?: boolean;
};

export const Level = ({ isOnboarding = false }: LevelProps) => {
  const { t } = useTranslation(["pass"]);
  const { status, auditPending, nextStatus, kycLevel } = useKYCContext();
  const { useUserProfile } = useUser();
  const { companyDetailQuery, companyDetailQueryAdmin } = useCompany();
  const { data: user, refetch, isRefetching: isLoadingUser } = useUserProfile();
  const { leUuid } = useCompanyContext();
  const { checkCompanyInfoActionsPermission } = useUserCheckAccess();
  const { completeCompanyDataMutation } = useCompany();
  const { mutateAsync: completeCompanyAdd, isPending: isLoadingCompanyComplete } =
    completeCompanyDataMutation();

  const userRoles = useAppSelector(getUserRoles);
  const isAdmin = userRoles.includes(role.ADMIN);
  const {
    data: companyUserDetails,
    refetch: refetchCompany,
    isFetching: isCompanyDataFetching,
    isLoading: isLoadingCompanyData
  } = companyDetailQuery(leUuid);
  const { useGetLinksFromLegalEntity } = useLinks();

  const { data: companyDetailsAdmin, isFetching: isPendingAdminDetails } = companyDetailQueryAdmin(
    leUuid ?? null
  );
  const companyDetails = companyUserDetails ?? companyDetailsAdmin;

  const { data: linksQuery } = useGetLinksFromLegalEntity();

  const isAgentOrAdmin = (() =>
    linksQuery?.find(
      (links) =>
        links.realIdentity.uuid === user?.naturalPerson.UUID &&
        links.type === OlkyPassProLinkType.AGENT
    ) || isAdmin)();

  const getDescription = (statusClicked: KYCStatusType | null) => {
    switch (statusClicked) {
      case KYCStatusType.AUTHENTICATED_GOLD:
        return leUuid ? olkyPassInfo[5].description_pro : olkyPassInfo[5].description;

      case KYCStatusType.AUTHENTICATED_SILVER:
        return leUuid ? olkyPassInfo[4].description_pro : olkyPassInfo[4].description;

      case KYCStatusType.AUTHENTICATED_BRONZE:
        return leUuid ? olkyPassInfo[3].description_pro : olkyPassInfo[3].description;

      case KYCStatusType.AUTHENTICATED:
        return leUuid ? olkyPassInfo[2].description_pro : olkyPassInfo[2].description;

      case KYCStatusType.VERIFIED:
        return leUuid ? olkyPassInfo[1].description_pro : olkyPassInfo[1].description;

      case KYCStatusType.DECLARATIVE:
        return leUuid ? olkyPassInfo[0].description_pro : olkyPassInfo[0].description;

      default:
        break;
    }
  };

  const olkyPassInfo = [
    {
      id: 0,
      title: t("olkypass.mainPage.olkypass.levelPage.status_declarative"),
      description: t("olkypass.mainPage.olkypass.levelPage.declarative_description"),
      sub_description: t("olkypass.mainPage.olkypass.levelPage.declarative_sub_description"),
      description_pro: t("olkypass.mainPage.olkypass.levelPage.declarative_description_pro"),
      subdescription_pt2: "",
      kyc: KYCStatusType.DECLARATIVE,
      lvl: 1
    },
    {
      id: 1,
      title: t("olkypass.mainPage.olkypass.levelPage.status_verified"),
      description: t("olkypass.mainPage.olkypass.levelPage.verified_description"),
      description1: t("olkypass.mainPage.olkypass.levelPage.verified_description_1"),
      description2: t("olkypass.mainPage.olkypass.levelPage.verified_description_2"),
      description3: t("olkypass.mainPage.olkypass.levelPage.verified_description_3"),
      description4: t("olkypass.mainPage.olkypass.levelPage.verified_description_4"),
      sub_description: t("olkypass.mainPage.olkypass.levelPage.verified_sub_description"),
      description_pro: t("olkypass.mainPage.olkypass.levelPage.verified_description_pro"),
      subdescription_pt2: "",
      kyc: KYCStatusType.VERIFIED,
      lvl: 2
    },
    {
      id: 2,
      title: t("olkypass.mainPage.olkypass.levelPage.status_authenticated"),
      description: t("olkypass.mainPage.olkypass.levelPage.authenticated_description"),
      description_pro: t("olkypass.mainPage.olkypass.levelPage.authenticated_description_pro"),

      sub_description: t("olkypass.mainPage.olkypass.levelPage.authenticated_sub_description"),
      subdescription_pt2: t(
        "olkypass.mainPage.olkypass.levelPage.authenticated_sub_description_pt2"
      ),
      kyc: KYCStatusType.AUTHENTICATED,
      lvl: 3
    },

    {
      id: 3,
      title: t("olkypass.mainPage.olkypass.levelPage.status_authenticated_bronz"),
      description: t("olkypass.mainPage.olkypass.levelPage.authenticated_bronz_description"),
      description_pro: t(
        "olkypass.mainPage.olkypass.levelPage.authenticated_bronz_description_pro"
      ),

      sub_description: t(
        "olkypass.mainPage.olkypass.levelPage.authenticated_bronz_sub_description"
      ),
      subdescription_pt2: t(
        "olkypass.mainPage.olkypass.levelPage.authenticated_bronz_sub_description_pt2"
      ),

      kyc: KYCStatusType.AUTHENTICATED_BRONZE,
      lvl: 4
    },
    {
      id: 4,
      title: t("olkypass.mainPage.olkypass.levelPage.status_authenticated_silver"),
      description: t("olkypass.mainPage.olkypass.levelPage.authenticated_silver_description"),
      description_pro: t(
        "olkypass.mainPage.olkypass.levelPage.authenticated_silver_description_pro"
      ),

      sub_description: t(
        "olkypass.mainPage.olkypass.levelPage.authenticated_silver_sub_description"
      ),
      subdescription_pt2: t(
        "olkypass.mainPage.olkypass.levelPage.authenticated_silver_sub_description_pt2"
      ),
      kyc: KYCStatusType.AUTHENTICATED_SILVER,
      lvl: 5
    },
    {
      id: 5,
      title: t("olkypass.mainPage.olkypass.levelPage.status_authenticated_gold"),
      description: t("olkypass.mainPage.olkypass.levelPage.authenticated_gold_description"),
      description_pro: t("olkypass.mainPage.olkypass.levelPage.authenticated_gold_description_pro"),

      sub_description: t("olkypass.mainPage.olkypass.levelPage.authenticated_gold_sub_description"),
      subdescription_pt2: t(
        "olkypass.mainPage.olkypass.levelPage.authenticated_gold_sub_description_pt2"
      ),
      kyc: KYCStatusType.AUTHENTICATED_GOLD,
      lvl: 6
    }
  ];

  const actionEnabled = checkCompanyInfoActionsPermission();

  const handleCompanyAddComplete = async () => {
    const legalMandate = linksQuery?.find(
      (link) =>
        link.agentNature?.defaultName === AgentNatureMandate.LEGAL_REPRESENTATIVE ||
        link.agentNature?.defaultName === AgentNatureMandate.JUDICIAL_AGENT
    );
    if (!legalMandate) {
      toast.error(t("business_links.validation.error"));
      return;
    }
    if (!leUuid) return;

    try {
      await completeCompanyAdd(leUuid);
      toast.success(t("business_links.validation.success_complete"));
    } catch (_) {
      toast.error(t("business_links.validation.error_complete"));
    }
  };

  return (
    <>
      {!isOnboarding && <LayoutTitle>{t("levelPage.title")}</LayoutTitle>}

      <div className="mb-2">
        <IdentityHeaderCard
          user={user}
          companyDetails={companyDetails}
          id={leUuid}
          className={twMerge(
            "mb-4 w-full rounded-sm border-2 border-slate-50 dark:md:border-slate-800"
          )}
        />
        {!leUuid ? (
          <></>
        ) : (
          <>
            {companyDetails?.legalEntity.IsWaitingConsent && (
              <Alert
                title={t("business_links.consentdocument.sign.alert_title")}
                className="mb-4"
                type={AlertType.Warning}
              >
                <InfoAlert className="mt-2 block h-full text-alert_info-text text-warning">
                  {t("business_links.consentdocument.sign.alert")}
                </InfoAlert>
              </Alert>
            )}
            {!isAdmin && (isLoadingCompanyData || isPendingAdminDetails) ? (
              <Loader />
            ) : (
              isAgentOrAdmin === undefined && (
                <Alert
                  className="px-4 py-6"
                  type={AlertType.Info}
                  title={t("business_links.alert.notAgent.title")}
                >
                  <div className="">
                    <InfoAlert className="mt-2 block h-full text-alert_info-text">
                      {t("business_links.alert.notAgent.description")}
                    </InfoAlert>
                  </div>
                </Alert>
              )
            )}
          </>
        )}
        {isPendingAdminDetails ? (
          <Loader></Loader>
        ) : (
          !companyDetails?.legalEntity?.AgentReviewFinish &&
          isAdmin &&
          !isOnboarding && (
            <Alert
              className="px-4 py-6"
              type={AlertType.Info}
              title={t("business_links.alert.admin.title")}
            >
              <div className="">
                <InfoAlert className="mt-2 block h-full text-alert_info-text">
                  {t("business_links.alert.admin.description_level")}
                </InfoAlert>
              </div>
              <Button
                size="md"
                className="mt-3 w-42 md:w-80 lg:mt-5"
                dataCy="admin-validate-company-button"
                theme={Themes.white}
                loading={isLoadingCompanyComplete}
                onClick={() => {
                  handleCompanyAddComplete();
                }}
                iconPosition="after"
              >
                {t("business_links.alert.admin.btn_text")}
              </Button>
            </Alert>
          )
        )}
      </div>

      <div className="flex flex-col-reverse justify-between 2xl:flex-row">
        <div className="mt-2 mb-4 block rounded-sm border-2 border-slate-50 p-4 md:mt-6 md:mb-4 2xl:hidden dark:md:border-slate-800">
          <LevelSupport />
        </div>
        <div className={twMerge("2xl:w-3/4", isAdmin && "2xl:w-full")}>
          <>
            <LevelCard
              status={KYCStatusType.DECLARATIVE}
              olkyPassInfo={olkyPassInfo}
              kycLevel={kycLevel}
              auditPending={auditPending}
              nextStatus={nextStatus}
              user={user}
              refetchCompany={refetchCompany}
              companyDetails={companyDetails}
              isCompanyDataFetching={isCompanyDataFetching}
              actionEnabled={actionEnabled}
              isAdmin={isAdmin}
            />
            <LevelCard
              status={KYCStatusType.VERIFIED}
              kycLevel={kycLevel}
              olkyPassInfo={olkyPassInfo}
              auditPending={auditPending}
              nextStatus={nextStatus}
              user={user}
              refetchCompany={refetchCompany}
              companyDetails={companyDetails}
              actionEnabled={actionEnabled}
              isAdmin={isAdmin}
            />
            <LevelCard
              olkyPassInfo={olkyPassInfo}
              status={KYCStatusType.AUTHENTICATED}
              kycLevel={kycLevel}
              auditPending={auditPending}
              nextStatus={nextStatus}
              isLoadingUser={isLoadingUser}
              user={user}
              refetchUser={refetch}
              refetchCompany={refetchCompany}
              companyDetails={companyDetails}
              actionEnabled={actionEnabled}
              isAdmin={isAdmin}
            />
            <LevelCard
              status={KYCStatusType.AUTHENTICATED_BRONZE}
              olkyPassInfo={olkyPassInfo}
              kycLevel={kycLevel}
              nextStatus={nextStatus}
              auditPending={auditPending}
              user={user}
              companyDetails={companyDetails}
              actionEnabled={actionEnabled}
              isAdmin={isAdmin}
            />
            <LevelCard
              status={KYCStatusType.AUTHENTICATED_SILVER}
              olkyPassInfo={olkyPassInfo}
              kycLevel={kycLevel}
              nextStatus={nextStatus}
              auditPending={auditPending}
              user={user}
              companyDetails={companyDetails}
              actionEnabled={actionEnabled}
              isAdmin={isAdmin}
            />
            <LevelCard
              status={KYCStatusType.AUTHENTICATED_GOLD}
              olkyPassInfo={olkyPassInfo}
              kycLevel={kycLevel}
              actionEnabled={actionEnabled}
              nextStatus={nextStatus}
              auditPending={auditPending}
              user={user}
              companyDetails={companyDetails}
              isAdmin={isAdmin}
            />
          </>
        </div>
        {!isAdmin && (
          <div className="mb-8 border-slate-400 md:h-50 lg:min-w-[440px] 2xl:ml-4 2xl:w-1/4 2xl:gap-x-6">
            <div className="rounded-sm border-2 border-slate-50 dark:md:border-slate-800">
              <div>
                <OlkypassCard
                  CTA={false}
                  verifyButton
                  currentStatus={status}
                  nextStatus={nextStatus}
                  auditPending={auditPending}
                  withoutButton={true}
                />
              </div>
              <div className="p-4">
                {auditPending ? (
                  <Base className="mt-4 text-sm">
                    {getDescription(nextStatus)}
                    {nextStatus === KYCStatusType.VERIFIED && !leUuid && (
                      <div className="mt-5 px-4">
                        <ul className="mb-4 list-disc">
                          <li>
                            {" "}
                            <Base className="mt-4 mb-2 text-sm">
                              {olkyPassInfo[1].description1}
                            </Base>
                          </li>
                          <li>
                            {" "}
                            <Base className="mt-4 mb-2 text-sm">
                              {olkyPassInfo[1].description2}
                            </Base>
                          </li>
                          <li>
                            {" "}
                            <Base className="mt-4 mb-2 text-sm">
                              {olkyPassInfo[1].description3}
                            </Base>
                          </li>
                        </ul>
                        <Base className="mt-4 mb-2 text-sm">{olkyPassInfo[1].description4}</Base>
                      </div>
                    )}
                  </Base>
                ) : (
                  <>
                    {" "}
                    <Base className="mt-4 mb-2 text-sm">{getDescription(status)}</Base>
                    {status === KYCStatusType.VERIFIED && !leUuid && (
                      <div className="mt-5 px-4">
                        <ul className="mb-4 list-disc">
                          <li>
                            {" "}
                            <Base className="mt-4 mb-2 text-sm">
                              {olkyPassInfo[1].description1}
                            </Base>
                          </li>
                          <li>
                            {" "}
                            <Base className="mt-4 mb-2 text-sm">
                              {olkyPassInfo[1].description2}
                            </Base>
                          </li>
                          <li>
                            {" "}
                            <Base className="mt-4 mb-2 text-sm">
                              {olkyPassInfo[1].description3}
                            </Base>
                          </li>
                        </ul>
                        <Base className="mt-4 mb-2 text-sm">{olkyPassInfo[1].description4}</Base>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            {!isAdmin && (
              <div className="mt-8 mb-4 hidden rounded-sm border-2 border-slate-50 p-4 md:mt-6 md:mb-0 2xl:block dark:md:border-slate-800">
                <LevelSupport />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
